import React, { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { DynamicTableV2 } from '@packages/table-builder';
import type { ExternalTableSchema } from '@packages/table-builder';
import { Typography } from '@packages/ui/primitives';
import { Divider } from '@packages/ui/layout';
import type { FormEmbeddedTableNode } from '../types/layout';
import { useFormEngineForm, useFormEngineState } from '../context/FormEngineContext';
import { TableSchemaRegistry } from '../resolvers/tableSchemaRegistry';
import { axiosInstance } from '@/services/apiInterceptor';
import {
  collectFormPointerWatchPaths,
  resolveFormPointerMap,
} from '../utils/resolveFormPointerMap';
import { bakeEmbeddedTableEndpoint } from '../utils/bakeEmbeddedTableEndpoint';
import { evaluateCondition } from '../evaluators/conditionEvaluator';
import { extractConditionFieldPaths } from '../utils/extractConditionFieldPaths';

interface FormEmbeddedTableRendererProps {
  node: FormEmbeddedTableNode;
}

export const FormEmbeddedTableRenderer = React.memo(function FormEmbeddedTableRenderer({
  node,
}: FormEmbeddedTableRendererProps): React.ReactElement | null {
  const { form } = useFormEngineForm();
  const { params: blockParams, meta, getOptionProp } = useFormEngineState();

  const baseSchema = TableSchemaRegistry.get(node.schemaRef) as
    | ExternalTableSchema
    | undefined;

  const hiddenWhenWatchPaths = useMemo(
    () => (node.hiddenWhen ? extractConditionFieldPaths(node.hiddenWhen) : []),
    [node.hiddenWhen],
  );

  const watchedFields = useMemo(() => {
    const fromFilter = collectFormPointerWatchPaths(node.filter);
    const fromParams = collectFormPointerWatchPaths(node.actionParamMap);
    return [...new Set([...fromFilter, ...fromParams, ...hiddenWhenWatchPaths])];
  }, [node.filter, node.actionParamMap, hiddenWhenWatchPaths]);

  const watchedValues = useWatch({
    control: form.control,
    name: watchedFields as never[],
    disabled: watchedFields.length === 0,
  });

  const resolvedFilter = useMemo(() => {
    if (!node.filter) return undefined;
    const fromForm = resolveFormPointerMap(node.filter, form.getValues()) ?? {};
    const merged = { ...fromForm };
    if (
      (merged.Id == null || merged.Id === '') &&
      blockParams?.planGrantDocumentId?.trim()
    ) {
      merged.Id = blockParams.planGrantDocumentId;
    }
    return merged;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.filter, watchedValues, blockParams?.planGrantDocumentId]);

  const resolvedActionParams = useMemo(() => {
    if (!node.actionParamMap) return undefined;
    const fromForm = resolveFormPointerMap(node.actionParamMap, form.getValues()) ?? {};
    const merged = { ...fromForm };
    if (
      (merged.planGrantDocumentId == null || merged.planGrantDocumentId === '') &&
      blockParams?.planGrantDocumentId?.trim()
    ) {
      merged.planGrantDocumentId = blockParams.planGrantDocumentId;
    }
    return merged;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.actionParamMap, watchedValues, blockParams?.planGrantDocumentId]);

  const handleEditStateChange = useCallback(
    (hasEdits: boolean) => {
      form.setValue('__embeddedTableDirty', (hasEdits ? true : false) as never, { shouldDirty: hasEdits });
    },
    [form],
  );

  const schema = useMemo(() => {
    if (!baseSchema) return null;
    let next = baseSchema;
    if (node.hideToolbar) {
      next = {
        ...next,
        actions: {
          ...(next.actions ?? {}),
          toolbar: { primary: undefined, secondary: [] },
        },
      };
    }
    return bakeEmbeddedTableEndpoint(next, resolvedFilter);
  }, [baseSchema, node.hideToolbar, resolvedFilter]);

  const isHiddenByCondition = useMemo(() => {
    if (!node.hiddenWhen) return false;
    return evaluateCondition(node.hiddenWhen, {
      values: form.getValues() as Record<string, unknown>,
      meta,
      getOptionProp,
    });
  }, [node.hiddenWhen, meta, getOptionProp, form, watchedValues]);

  if (isHiddenByCondition) {
    return null;
  }

  if (!schema) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[form-engine] embedded-table: no schema found for ref "${node.schemaRef}". ` +
          'Register it via TableSchemaRegistry.register() at app startup.',
      );
    }
    return null;
  }

  const embeddedFeatures = {
    layout: { noPadding: true },
    ...(node.features as Record<string, unknown> | undefined),
  };

  return (
    <div data-embedded-table={node.schemaRef}>
      {node.title && (
        <>
          <Divider />
          <Typography variant='body-s' fontWeight={500} style={{ padding: '12px 0 4px' }}>
            {node.title}
          </Typography>
        </>
      )}
      <DynamicTableV2
        schema={{ ...(schema as any), features: { ...(schema as any).features, ...embeddedFeatures } }}
        axiosInstance={axiosInstance as any}
        semanticId={node.semanticId ?? `embedded:${node.schemaRef}`}
        onEditStateChange={handleEditStateChange}
        {...(resolvedFilter != null && { filter: resolvedFilter })}
        {...(resolvedActionParams != null && { actionAmbientParams: resolvedActionParams })}
      />
    </div>
  );
});
